import React, { useState, useCallback, useRef, useEffect } from "react";
import { Dropdown, DropdownItem } from "@scripts/react/components/Dropdown/Dropdown";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { useMobile } from "@/hooks/useMobile";

interface VideoSpeedControlProps {
    videoRef?: React.RefObject<HTMLVideoElement | null>;
    disabled: boolean;
}

const SPEED_OPTIONS: DropdownItem[] = [
    { text: "Speed", value: "header", className: "header" },
    { text: "0.5x", value: 0.5 },
    { text: "0.75x", value: 0.75 },
    { text: "1.0x", value: 1 },
    { text: "1.25x", value: 1.25 },
    { text: "1.5x", value: 1.5 },
    { text: "1.75x", value: 1.75 },
    { text: "2.0x", value: 2 }
];

export const VideoSpeedControl: React.FC<VideoSpeedControlProps> = ({ videoRef, disabled }) => {
    const [currentSpeed, setCurrentSpeed] = useState<number>(1);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const isMobile = useMobile(); // Using shared hook for sm breakpoint (640px)
    const speedDropdownRef = useRef<HTMLDivElement>(null);

    const handleSpeedChange = useCallback(
        (speed: number) => {
            if (videoRef?.current && !disabled) {
                videoRef.current.playbackRate = speed;
                setCurrentSpeed(speed);
            }
        },
        [videoRef, disabled]
    );

    const handleItemSelected = useCallback(
        (item: DropdownItem) => {
            if (item.value !== "header" && typeof item.value === "number") {
                handleSpeedChange(item.value);
            }
        },
        [handleSpeedChange]
    );

    const currentSpeedText =
        SPEED_OPTIONS.find((option) => option.value === currentSpeed)?.text || `${currentSpeed}x`;

    // Monitor dropdown open state by checking for dropdown menu in DOM
    useEffect(() => {
        const checkDropdownState = () => {
            if (speedDropdownRef.current) {
                const speedMenu = speedDropdownRef.current.querySelector('[role="menu"]');
                setIsDropdownOpen(!!speedMenu);
            }
        };

        const interval = setInterval(checkDropdownState, 100);
        return () => clearInterval(interval);
    }, []);

    return (
        <TooltipContainer
            type="titleOnly"
            title="Playback Speed"
            disabled={isDropdownOpen || isMobile || disabled}
            customButton={
                <div ref={speedDropdownRef}>
                    <Dropdown
                        title={currentSpeedText as string}
                        items={SPEED_OPTIONS}
                        onItemSelected={handleItemSelected}
                        menuButtonSize="xxsmall"
                        buttonClassName="btn2 w-10 !h-8 ghost !justify-center"
                        icon="hidden"
                        dropdownMenuStyling="!gap-0 mb-1"
                        disabled={disabled}
                    />
                </div>
            }
        />
    );
};
